import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { SettingsMainGrid } from '@/components/settings/SettingsParts'
import { Container, ContainerContent } from '@/components/ui/Container'
import { toast } from '@/hooks/use-toast'
import {
  applyAutomationToSettings,
  applyDateBehaviorToSettings,
  applyErpSyncToSettings,
  applyLeadTimeToSettings,
  applyLineFlagsToSettings,
  applyParseFormatToSettings,
  applyThresholdsAndFlagsToSettings,
  automationFromSettings,
  dateBehaviorFromSettings,
  erpSyncFromSettings,
  flaggedFieldsFromSettings,
  flaggedLineFieldsFromSettings,
  leadTimeFromSettings,
  parseFormatFromSettings,
  settingsEqual,
  thresholdsFromSettings,
  toUpdateRequest,
  type DateBehaviorSettings,
  type DeviationThresholds,
  type ParseFormatSettings,
} from '@/features/order-confirmation/settings/orderConfirmationSettingsMappers'
import {
  AdvancedSettingsSection,
  DateBehaviorSection,
  DeviationThresholdsSection,
  ErpSyncSection,
  FlagDifferencesSection,
  FullyAutomaticValidationSection,
  LeadTimeSection,
  ParseFormatSection,
} from '@/features/order-confirmation/settings/OrderConfirmationSettingsSections'
import {
  OC_HEAD_COMPARISON_FIELDS,
  type OcHeadComparisonFieldId,
} from '@/features/order-confirmation/settings/ocHeadComparisonFields'
import {
  OC_LINE_COMPARISON_FIELDS,
} from '@/features/order-confirmation/settings/ocLineComparisonFields'
import type { OcLineComparisonFlagFieldId } from '@/types/orderConfirmationSettings'
import { saveWorkflowOrderConfirmationSettings } from '@/services/api'
import {
  useWorkflowOrderConfirmationSettings,
  workflowOrderConfirmationSettingsQueryKey,
} from '@/features/order-confirmation/workflowOrderConfirmationSettingsQueries'
import type { OrderConfirmationSettings } from '@/types/orderConfirmationSettings'

export function SettingsPage() {
  const { workflowId } = useParams<{ workflowId: string }>()
  const queryClient = useQueryClient()

  const [savedSettings, setSavedSettings] = useState<OrderConfirmationSettings | null>(null)
  const [draftSettings, setDraftSettings] = useState<OrderConfirmationSettings | null>(null)

  const { data, isLoading, isError, error } = useWorkflowOrderConfirmationSettings(workflowId)

  useEffect(() => {
    if (data) {
      setSavedSettings(data)
      setDraftSettings(data)
    }
  }, [data])

  const draftThresholds = draftSettings ? thresholdsFromSettings(draftSettings) : null
  const draftDateBehavior = draftSettings ? dateBehaviorFromSettings(draftSettings) : null
  const draftParseFormat = draftSettings ? parseFormatFromSettings(draftSettings) : null
  const draftLeadTime = draftSettings ? leadTimeFromSettings(draftSettings) : null
  const draftAutomation = draftSettings ? automationFromSettings(draftSettings) : null
  const draftErpSync = draftSettings ? erpSyncFromSettings(draftSettings) : null
  const draftFlaggedFields = draftSettings ? flaggedFieldsFromSettings(draftSettings) : null
  const draftFlaggedLineFields = draftSettings ? flaggedLineFieldsFromSettings(draftSettings) : null

  const hasUnsavedChanges =
    savedSettings && draftSettings ? !settingsEqual(savedSettings, draftSettings) : false

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!workflowId || !draftSettings) {
        throw new Error('Settings are not loaded')
      }
      return saveWorkflowOrderConfirmationSettings(workflowId, toUpdateRequest(draftSettings))
    },
    onSuccess: (updated) => {
      setSavedSettings(updated)
      setDraftSettings(updated)
      queryClient.setQueryData(workflowOrderConfirmationSettingsQueryKey(workflowId!), updated)
      void queryClient.invalidateQueries({ queryKey: ['purchase-order-validation', workflowId] })
      toast({ title: 'Settings saved' })
    },
    onError: (err: Error) => {
      toast({
        title: 'Could not save settings',
        description: err.message,
        variant: 'destructive',
      })
    },
  })

  function updateDraftFromThresholds(thresholds: DeviationThresholds) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      return applyThresholdsAndFlagsToSettings(
        prev,
        thresholds,
        flaggedFieldsFromSettings(prev),
      )
    })
  }

  function toggleDateBehavior(key: keyof DateBehaviorSettings, checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      const dateBehavior = dateBehaviorFromSettings(prev)
      dateBehavior[key] = checked
      return applyDateBehaviorToSettings(prev, dateBehavior)
    })
  }

  function toggleParseFormat(key: keyof ParseFormatSettings, checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      const parseFormat = parseFormatFromSettings(prev)
      parseFormat[key] = checked
      return applyParseFormatToSettings(prev, parseFormat)
    })
  }

  function toggleUseLeadTime(checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      return applyLeadTimeToSettings(prev, {
        ...leadTimeFromSettings(prev),
        useLeadTimeWhenCalculatingArrival: checked,
      })
    })
  }

  function updateLeadTimeInDays(days: number) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      return applyLeadTimeToSettings(prev, {
        ...leadTimeFromSettings(prev),
        leadTimeInDays: days,
      })
    })
  }

  function toggleAutomation(checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      return applyAutomationToSettings(prev, { fullyAutomaticValidation: checked })
    })
  }

  function toggleErpSync(checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      return applyErpSyncToSettings(prev, { updateSupplierOrderNumberOnErpSync: checked })
    })
  }

  function toggleFlaggedLineField(id: OcLineComparisonFlagFieldId, checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      const flagged = flaggedLineFieldsFromSettings(prev)
      if (checked) flagged.add(id)
      else flagged.delete(id)
      return applyLineFlagsToSettings(prev, flagged)
    })
  }

  function toggleAllFlaggedLineFields(checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      const flagged = checked
        ? new Set(OC_LINE_COMPARISON_FIELDS.map((field) => field.id))
        : new Set<OcLineComparisonFlagFieldId>()
      return applyLineFlagsToSettings(prev, flagged)
    })
  }

  function toggleFlaggedField(id: OcHeadComparisonFieldId, checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      const flagged = flaggedFieldsFromSettings(prev)
      if (checked) flagged.add(id)
      else flagged.delete(id)
      return applyThresholdsAndFlagsToSettings(prev, thresholdsFromSettings(prev), flagged)
    })
  }

  function toggleAllFlaggedFields(checked: boolean) {
    setDraftSettings((prev) => {
      if (!prev) return prev
      const flagged = checked
        ? new Set(OC_HEAD_COMPARISON_FIELDS.map((field) => field.id))
        : new Set<OcHeadComparisonFieldId>()
      return applyThresholdsAndFlagsToSettings(prev, thresholdsFromSettings(prev), flagged)
    })
  }

  if (!workflowId) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-500">
        No workflow selected.
      </div>
    )
  }

  if (isLoading || !draftSettings || !draftThresholds || !draftDateBehavior || !draftParseFormat || !draftLeadTime || !draftAutomation || !draftErpSync || !draftFlaggedFields || !draftFlaggedLineFields) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-400">Loading…</div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-red-600">
        {error instanceof Error ? error.message : 'Could not load settings'}
      </div>
    )
  }

  return (
    <div className="container mx-auto">
      <Container>
        <ContainerContent className="space-y-8 py-6">
          <header className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
              <p className="mt-2 text-sm text-gray-600">
                Changes to these settings will affect all users of the organization
              </p>
            </div>
            <Button variant="cta"
              type="button"
              disabled={!hasUnsavedChanges || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </header>

          <FullyAutomaticValidationSection
            automation={draftAutomation}
            onToggle={toggleAutomation}
          />
          <SettingsMainGrid
            wide={
              <FlagDifferencesSection
                flaggedFields={draftFlaggedFields}
                onToggle={toggleFlaggedField}
                onToggleAll={toggleAllFlaggedFields}
                flaggedLineFields={draftFlaggedLineFields}
                onToggleLine={toggleFlaggedLineField}
                onToggleAllLine={toggleAllFlaggedLineFields}
              />
            }
            narrow={
              <>
                <DeviationThresholdsSection
                  thresholds={draftThresholds}
                  onDraftChange={updateDraftFromThresholds}
                />
                <ErpSyncSection erpSync={draftErpSync} onToggle={toggleErpSync} />
              </>
            }
          />
          <AdvancedSettingsSection>
            {/* Dates first: lead time and date behaviour both decide the arrival date compared. */}
            <div className="space-y-6">
              <LeadTimeSection
                leadTime={draftLeadTime}
                onToggleUseLeadTime={toggleUseLeadTime}
                onLeadTimeInDaysChange={updateLeadTimeInDays}
              />
              <DateBehaviorSection dateBehavior={draftDateBehavior} onToggle={toggleDateBehavior} />
            </div>
            <ParseFormatSection parseFormat={draftParseFormat} onToggle={toggleParseFormat} />
          </AdvancedSettingsSection>
        </ContainerContent>
      </Container>
    </div>
  )
}
